import { RefObject, useEffect } from 'react';
import {
  EngagementEntityType,
  EngagementSource,
} from 'constants/payloads/club/engagementBeacon';
import { trackImpression } from 'services/client/analytics/engagement';

interface Params {
  entityType: EngagementEntityType;
  entityId: string;
  source: EngagementSource;
  placementId?: string | null;
  /** Fraction of the element visible to count (default 0.5). */
  threshold?: number;
  /** Continuous ms the element must stay visible before it counts (default 1000). */
  dwellMs?: number;
  /** Skip while data isn't ready. */
  disabled?: boolean;
}

/**
 * Fires a single `impression` when `ref` is ≥`threshold` visible for `dwellMs`,
 * so a fast scroll-past doesn't count. Dedup across the page session is handled
 * by `trackImpression`.
 */
export function useEngagementImpression<T extends Element>(
  ref: RefObject<T>,
  {
    entityType,
    entityId,
    source,
    placementId = null,
    threshold = 0.5,
    dwellMs = 1000,
    disabled = false,
  }: Params,
) {
  useEffect(() => {
    const el = ref.current;
    if (!el || disabled || !entityId || typeof IntersectionObserver === 'undefined') return;

    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    let fired = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !fired) {
          dwellTimer = setTimeout(() => {
            fired = true;
            trackImpression({ entityType, entityId, source, placementId });
            observer.disconnect();
          }, dwellMs);
        } else if (!entry.isIntersecting && dwellTimer) {
          clearTimeout(dwellTimer);
          dwellTimer = null;
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (dwellTimer) clearTimeout(dwellTimer);
    };
  }, [ref, entityType, entityId, source, placementId, threshold, dwellMs, disabled]);
}
